<template>
  <StandardEmptyPageContainer>
    <StandardCardPageContainer :include-navigation="true">
      <template v-if="!isLoading" #title>
        <div v-if="forwardingMode">Forward Questionnaire</div>
        <div v-else>
          <div>Send Questionnaire:</div>
          <div>{{ senderStore.questionnaireLabel }}</div>
        </div>
      </template>

      <LoadingCircle v-if="isLoading" />

      <div v-else>
        <SelectReceiverCandidates
          v-if="!displayReviewEmailText"
          :sender-is-tlc="senderIsTLC"
          :receiver-candidates="receiverCandidates"
          :selected="selectedReceiverCandidates"
          :selected-date="dueDate"
          @due-date-updated="setDueDate"
          @receivers-updated="setReceiverCandidates"
          @suppliers-updated="fetchReceiverCandidates(true)"
        />

        <ReviewEmailAndReceivers
          v-if="displayReviewEmailText"
          :sender-is-tlc="senderIsTLC"
          :receiver-candidates="selectedReceiverCandidates"
          :due-date="dueDate"
        />
      </div>

      <template #backButton v-if="forwardingMode || displayReviewEmailText">
        <PActionButton
          class="me-auto"
          appkit-icon="icon-left-chevron-fill"
          :icon-size="'xs'"
          :prepend-icon="true"
          :variant="'outlined'"
          @click="
            displayReviewEmailText
              ? (displayReviewEmailText = false)
              : $emit('back')
          "
          >Back
        </PActionButton>
      </template>

      <template #saveButton v-if="forwardingMode">
        <PTextButton class="mr-2" @click="$router.push(RouterPath.Home)"
          >Continue later
        </PTextButton>
      </template>

      <template #nextButton>
        <PActionButton
          :append-icon="forwardingMode"
          :icon-size="'xs'"
          :disabled="isSendBtnDisabled"
          :loading="isSending"
          appkit-icon="icon-right-chevron-fill"
          @click="
            !displayReviewEmailText ? (displayReviewEmailText = true) : send()
          "
        >
          {{ !displayReviewEmailText ? "Preview email" : "Send" }}
        </PActionButton>
      </template>
    </StandardCardPageContainer>
  </StandardEmptyPageContainer>
</template>

<script setup lang="ts">
import { ReceiverCandidate } from "@/models/receiverCandidate";
import {
  createDistribution,
  createDistributions,
  getReceiverCandidatesForQuestionnaire,
} from "@/services/distributionService";
import { computed } from "vue";
import { onBeforeMount, onMounted, onUnmounted, ref } from "vue";
import LoadingCircle from "@/components/custom/LoadingCircle.vue";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { useSenderStore } from "@/stores/sender";
import StandardCardPageContainer from "@/components/containers/StandardCardPageContainer.vue";
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import PTextButton from "@/components/custom/buttons/PTextButton.vue";
import { RouterPath } from "@/models/enum/routerPath";
import StandardEmptyPageContainer from "@/components/containers/StandardEmptyPageContainer.vue";
import SelectReceiverCandidates from "@/views/questionnaires/SelectReceiverCandidates.vue";
import ReviewEmailAndReceivers from "@/views/questionnaires/ReviewEmailAndReceivers.vue";
import { useSelectedQuestionnaire } from "@/stores/selectedQuestionnare";

withDefaults(
  defineProps<{
    forwardingMode?: boolean;
  }>(),
  {
    forwardingMode: false,
  }
);

const emit = defineEmits<{
  (e: "back"): void;
  (e: "sent"): void;
}>();

const selectedQuestionnaireStore = useSelectedQuestionnaire();
const senderStore = useSenderStore();
const snackBarStore = useSnackBarStore();
const displayReviewEmailText = ref(false);
const senderIsTLC = ref(false);
const isLoading = ref(false);
const isSending = ref(false);
const receiverCandidates = ref(new Array<ReceiverCandidate>());
const selectedReceiverCandidates = ref(new Array<ReceiverCandidate>());

const defaultDueDate = new Date(new Date().setDate(new Date().getDate() + 14));
const dueDate = ref(defaultDueDate);

const isSendBtnDisabled = computed(
  () =>
    selectedReceiverCandidates.value == null ||
    selectedReceiverCandidates.value.length === 0
);

const hubConnection = new HubConnectionBuilder()
  .withUrl("/hub/distributions")
  .build();

onBeforeMount(async () => {
  await fetchReceiverCandidates(true);
  senderIsTLC.value = senderStore.isQuestionnairesOwner;
});

onMounted(() => {
  hubConnection.on("DistributionUpdate", (message: string) => {
    const updatedReceiverCandidate: ReceiverCandidate = JSON.parse(message);

    let candidate: ReceiverCandidate | undefined =
      receiverCandidates.value.find(
        (x: ReceiverCandidate) =>
          x.email == updatedReceiverCandidate.email &&
          x.companyName == updatedReceiverCandidate.companyName &&
          x.countryCode == updatedReceiverCandidate.countryCode &&
          x.organisationNumber == updatedReceiverCandidate.organisationNumber
      );

    if (candidate != undefined) {
      candidate.emailsSent = candidate.emailsSent + 1;
      candidate.distributionStatus =
        updatedReceiverCandidate.distributionStatus;
    }
  });

  hubConnection.start().catch((err: any) => console.error(err));
});

onUnmounted(async () => {
  await hubConnection.stop();
});

async function fetchReceiverCandidates(showSpinner: boolean): Promise<void> {
  isLoading.value = showSpinner;
  try {
    receiverCandidates.value = await getReceiverCandidatesForQuestionnaire(
      senderStore.questionnaireId
    );
    if (!senderStore.canSendToBP) {
      receiverCandidates.value = receiverCandidates.value.filter(
        (r) => r.isBusinessPartner == false
      );
    }
  } catch {
    snackBarStore.showError();
  } finally {
    isLoading.value = false;
  }
}

async function send(): Promise<void> {
  try {
    isSending.value = true;
    const dueDateUTC = new Date(
      Date.UTC(
        dueDate.value.getFullYear(),
        dueDate.value.getMonth(),
        dueDate.value.getDate()
      )
    );

    if (selectedQuestionnaireStore.questionnaires.length == 0) {
      await createDistribution(
        senderStore.questionnaireId,
        selectedReceiverCandidates.value,
        dueDateUTC
      );
    } else {
      await createDistributions(
        selectedQuestionnaireStore.questionnaires.map((x) => x.questionnaireId),
        selectedReceiverCandidates.value,
        dueDateUTC
      );
    }

    selectedReceiverCandidates.value = new Array<ReceiverCandidate>();
    await fetchReceiverCandidates(true);
    emit("sent");
  } catch {
    snackBarStore.showError();
  } finally {
    isSending.value = false;
  }
}

function setDueDate(d: Date) {
  dueDate.value = d;
}

function setReceiverCandidates(r: Array<ReceiverCandidate>) {
  selectedReceiverCandidates.value = r;
}
</script>
